// CompassRose — the directional instrument: a fine tick ring (cardinals or
// ±180° relative), any number of pointers layered without clutter (thin
// needle from center, rim chevrons, rim ticks), soft sector arcs for
// port/starboard or shift ranges, and free center space for readouts.
// Pointers sweep the short way around on update (359°→1° turns 2°, not 358°).

import { memo, useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import { arcPath, normalizeAngle, polarXY, unwindAngle } from './lib/arc';
import { applyRotate, useSmoothedNumber } from './lib/useSmoothedNumber';
import { seriesTone, toneColor, type Tone } from './types';

export type RosePointerKind = 'needle' | 'chevron' | 'tick';

export interface RosePointer {
  id: string;
  /** Degrees, in the RING's frame — compass mode: a bearing (0 = N), which
      course-up (`ringRotation`) rotates together with the ring; relative
      mode: −180…180, 0 = dead ahead. Boat-frame data on a course-up compass
      rose (e.g. AWA) converts to a bearing first: heading + angle. */
  angle: number | null;
  /** Display time of this reading (ms, local clock). Supplying it puts the
      pointer in timeline mode: samples are buffered and played back on a fixed
      lag, interpolated between, when samples arrive close enough together for
      that to help (see `motion.ts`). Either way the pointer moves continuously.
      Omit it and the pointer springs at each new angle as it arrives — the
      behavior every existing call site already gets. */
  at?: number;
  kind?: RosePointerKind;
  tone?: Tone;
  /** Legend text ("TWD 067°"). Labeled pointers get a legend row. */
  label?: string;
  /** A live reading for the legend ("-135°"), drawn after `label` in a slot
      wide enough for "-180°", so the legend keeps its width and position as
      the number changes. */
  value?: string;
  /** One or two letters knocked out of a chevron ("A", "T") — instrument
      convention for telling apparent from true at a glance. */
  badge?: string;
}

export interface RoseSector {
  from: number;
  to: number;
  tone?: Tone;
}

export interface CompassRoseProps {
  /** 'compass': 0–360 ring with cardinals. 'relative': ±180, 0 up. */
  mode?: 'compass' | 'relative';
  pointers?: RosePointer[];
  /** Soft arc bands on the ring (laylines, close-hauled zones, shift range). */
  sectors?: RoseSector[];
  /** Rotates the ring for course-up displays (pass the heading). */
  ringRotation?: number;
  /** 'fine' adds 2° micro-ticks between the 10° minors (precision look). */
  ticks?: 'standard' | 'fine';
  /** Ring text: 'upright' (modern instrument, default) or 'tangent' —
      rotated along the card like a physical compass rose. */
  labelOrientation?: 'upright' | 'tangent';
  /** Content for the middle — usually a <Readout>. */
  center?: ReactNode;
  /** Opaque surface disc under the center content; pointers slide behind
      it with only their tips showing (default: on when `center` is set). */
  centerDisc?: boolean;
  /** Legend under the rose for labeled pointers (default: auto). */
  legend?: boolean;
  ariaLabel?: string;
  className?: string;
  /** The kind of reading the rose's accent pointers show (`data-kind`,
      charts.css): a helm's true wind is wind, whatever its card's kind. */
  kind?: string;
}

const C = 110; // viewBox center
const R_OUT = 96;
const R_MINOR = 90;
const R_MAJOR = 87;
const R_TEXT = 74;
const R_SECTOR = 92.5;
const R_DISC = 54; // ≈56% of the ring, the reference instrument proportion

export function CompassRose({
  mode = 'compass',
  pointers = [],
  sectors = [],
  ringRotation = 0,
  ticks = 'standard',
  labelOrientation = 'upright',
  center,
  centerDisc,
  legend,
  ariaLabel,
  className,
  kind,
}: CompassRoseProps) {
  const showLegend = legend ?? pointers.some((p) => p.label !== undefined);
  const showDisc = centerDisc ?? center !== undefined;
  // The ring stays CSS-animated rather than joining the imperative path: its
  // upright labels counter-rotate at RENDER time (each <text> needs the
  // current ring angle to stay readable), so it cannot be driven by writing a
  // single transform. It also moves with heading, which changes far more
  // slowly than wind — a CSS transition is the right tool for it.
  const ringAngle = useUnwoundAngle(-ringRotation);

  return (
    <div className={cx('chart-rose', className)} data-kind={kind}>
      <div className="chart-rose-box" role="img" aria-label={ariaLabel ?? 'compass'}>
        <svg className="chart-rose-svg" viewBox={`0 0 ${C * 2} ${C * 2}`}>
          <g className="chart-rose-ring" style={rotateStyle(ringAngle)}>
            {sectors.map((s, i) => (
              <path
                key={`sec-${i}`}
                d={arcPath(C, C, R_SECTOR, s.from, s.to)}
                fill="none"
                stroke={toneColor(s.tone ?? 'accent')}
                strokeWidth={7}
                opacity={0.3}
              />
            ))}
            <Ring
              mode={mode}
              fine={ticks === 'fine'}
              upright={ringAngle}
              orientation={labelOrientation}
            />
          </g>
          {pointers.map((p, i) => (
            <Pointer key={p.id} pointer={p} index={i} disc={showDisc} ringRotation={ringRotation} />
          ))}
          {showDisc && (
            <circle
              cx={C}
              cy={C}
              r={R_DISC}
              fill="var(--chart-surface)"
              stroke="var(--chart-track)"
              strokeWidth={1}
            />
          )}
        </svg>
        {center !== undefined && <div className="chart-rose-center">{center}</div>}
      </div>
      {showLegend && (
        // One row per pointer, in three columns: the key, the label and the
        // reading. The key is the rim chevron's triangle in the pointer's tone.
        <div className="chart-legend chart-rose-legend">
          {pointers
            .map((p, i) => ({ p, i }))
            .filter(({ p }) => p.label !== undefined)
            .map(({ p, i }) => (
              <span key={p.id} className="chart-legend-item">
                <svg
                  className="chart-rose-legend-key"
                  viewBox="0 0 10 9"
                  aria-hidden="true"
                  style={{ '--chart-tone': toneColor(p.tone ?? seriesTone(i)) } as CSSProperties}
                >
                  <polygon points="0,0 10,0 5,9" />
                </svg>
                <span>{p.label}</span> <span className="chart-legend-value">{p.value ?? ''}</span>
              </span>
            ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- pieces

// Memoized: a fine ring is ~180 SVG nodes, and it depends only on heading —
// which changes far more slowly than the wind feed driving the pointers.
// Without this the whole ring reconciles on every live sample, which costs
// more than the animation it surrounds.
const Ring = memo(function Ring({
  mode,
  fine,
  upright,
  orientation,
}: {
  mode: 'compass' | 'relative';
  fine: boolean;
  /** Ring rotation to counter — labels stay upright on a course-up card. */
  upright: number;
  orientation: 'upright' | 'tangent';
}) {
  // 'tangent' rotates each label along the card (it then turns WITH the
  // ring, like a physical compass rose); 'upright' counters the rotation.
  const labelTransform = (x: number, y: number, a: number): string | undefined =>
    orientation === 'tangent'
      ? `rotate(${a} ${x} ${y})`
      : upright === 0
        ? undefined
        : `rotate(${-upright} ${x} ${y})`;
  const minors: ReactNode[] = [];
  if (fine) {
    for (let a = 0; a < 360; a += 2) {
      if (a % 10 === 0) continue;
      const p1 = polarXY(C, C, 93.5, a);
      const p2 = polarXY(C, C, R_OUT, a);
      minors.push(
        <line
          key={`f-${a}`}
          x1={p1.x}
          y1={p1.y}
          x2={p2.x}
          y2={p2.y}
          stroke="var(--chart-track)"
          strokeWidth={0.75}
          opacity={0.7}
        />
      );
    }
  }
  for (let a = 0; a < 360; a += 10) {
    if (a % 30 === 0) continue;
    const p1 = polarXY(C, C, R_MINOR, a);
    const p2 = polarXY(C, C, R_OUT, a);
    minors.push(
      <line
        key={`mi-${a}`}
        x1={p1.x}
        y1={p1.y}
        x2={p2.x}
        y2={p2.y}
        stroke="var(--chart-track)"
        strokeWidth={1}
      />
    );
  }
  const majors: ReactNode[] = [];
  for (let a = 0; a < 360; a += 30) {
    const p1 = polarXY(C, C, R_MAJOR, a);
    const p2 = polarXY(C, C, R_OUT, a);
    const isNorth = mode === 'compass' && a === 0;
    majors.push(
      <line
        key={`ma-${a}`}
        x1={p1.x}
        y1={p1.y}
        x2={p2.x}
        y2={p2.y}
        stroke={isNorth ? 'var(--chart-north)' : 'var(--chart-axis)'}
        strokeWidth={isNorth ? 2.5 : 1.5}
      />
    );
  }
  const labels: ReactNode[] = [];
  if (mode === 'compass') {
    const cardinals: Array<[number, string]> = [
      [0, 'N'],
      [90, 'E'],
      [180, 'S'],
      [270, 'W'],
    ];
    for (const [a, letter] of cardinals) {
      const p = polarXY(C, C, R_TEXT, a);
      labels.push(
        <text
          key={`c-${letter}`}
          className={`chart-rose-cardinal${letter === 'N' ? ' chart-rose-cardinal--n' : ''}`}
          x={p.x}
          y={p.y + 4.5}
          textAnchor="middle"
          transform={labelTransform(p.x, p.y, a)}
        >
          {letter}
        </text>
      );
    }
    for (let a = 30; a < 360; a += 30) {
      if (a % 90 === 0) continue;
      const p = polarXY(C, C, R_TEXT, a);
      labels.push(
        <text
          key={`d-${a}`}
          className="chart-rose-minor"
          x={p.x}
          y={p.y + 3.5}
          textAnchor="middle"
          transform={labelTransform(p.x, p.y, a)}
        >
          {String(a).padStart(3, '0')}
        </text>
      );
    }
  } else {
    for (let a = -150; a <= 180; a += 30) {
      if (a === 0 || a === 180) {
        const p = polarXY(C, C, R_TEXT, a);
        labels.push(
          <text
            key={`r-${a}`}
            className="chart-rose-cardinal"
            x={p.x}
            y={p.y + 4.5}
            textAnchor="middle"
            transform={labelTransform(p.x, p.y, a)}
          >
            {a === 0 ? '0' : '180'}
          </text>
        );
        continue;
      }
      const p = polarXY(C, C, R_TEXT, a);
      labels.push(
        <text
          key={`r-${a}`}
          className="chart-rose-minor"
          x={p.x}
          y={p.y + 3.5}
          textAnchor="middle"
          transform={labelTransform(p.x, p.y, a)}
        >
          {Math.abs(a)}
        </text>
      );
    }
  }
  return (
    <g>
      {minors}
      {majors}
      {labels}
    </g>
  );
});

function Pointer({
  pointer,
  index,
  disc,
  ringRotation,
}: {
  pointer: RosePointer;
  index: number;
  disc: boolean;
  ringRotation: number;
}) {
  // Hooks run UNCONDITIONALLY, before any early return. The previous code
  // returned null for a null angle before calling its hook, so a dropout
  // unmounted the needle and remounted it from an implicit 0 — the needle
  // swung up through north on every recovery. Now the group stays mounted and
  // simply hides, so the smoother keeps its state and the needle freezes where
  // it was.
  const hasValue = pointer.angle !== null;
  // A pointer that has NEVER had a value stays hidden — there is nothing
  // honest to point at. One that HAD a value and lost it stays visible and
  // frozen, with the card's staleness dimming telling the real story.
  const everHadValue = useRef(false);
  if (hasValue) everHadValue.current = true;
  // Pointer angles are ring-frame: course-up rotation carries them along.
  const ref = useSmoothedNumber<SVGGElement>(
    hasValue ? pointer.angle! - ringRotation : null,
    pointer.at,
    ROTATE_ABOUT_CENTER,
    { angular: true }
  );
  const color = toneColor(pointer.tone ?? seriesTone(index));
  const kind = pointer.kind ?? 'needle';
  return (
    <g
      ref={ref}
      className="chart-rose-pointer"
      style={{ visibility: everHadValue.current ? undefined : 'hidden' }}
    >
      {kind === 'needle' && disc && (
        // Blade: tucked under the disc, running out to the ring — visible
        // along its whole length instead of a sliver past the disc edge.
        <polygon
          points={`0,${-(R_MAJOR - 3)} 4.5,${-(R_DISC - 10)} -4.5,${-(R_DISC - 10)}`}
          transform={`translate(${C},${C})`}
          fill={color}
        />
      )}
      {kind === 'needle' && !disc && (
        <>
          <polygon
            points={`0,${-(R_MINOR - 26)} 5,6 -5,6`}
            transform={`translate(${C},${C})`}
            fill={color}
          />
          <circle
            cx={C}
            cy={C}
            r={5.5}
            fill={color}
            stroke="var(--chart-surface)"
            strokeWidth={2}
          />
        </>
      )}
      {kind === 'chevron' && (
        <>
          <polygon
            points={`0,${-(R_MAJOR - 1)} 6.5,${-(R_OUT + 7)} -6.5,${-(R_OUT + 7)}`}
            transform={`translate(${C},${C})`}
            fill={color}
            stroke="var(--chart-surface)"
            strokeWidth={1.5}
            strokeLinejoin="round"
          />
          {pointer.badge !== undefined && (
            // Knockout letter: parts outside the triangle vanish into the
            // card (same color), so the whole glyph must sit inside it —
            // baseline chosen so the cap height clears the chevron's base.
            <text
              x={C}
              y={C - R_OUT + 0.5}
              textAnchor="middle"
              fontSize={7}
              fontWeight={700}
              fill="var(--chart-surface)"
            >
              {pointer.badge}
            </text>
          )}
        </>
      )}
      {kind === 'tick' && (
        <line
          x1={C}
          y1={C - R_MAJOR + 2}
          x2={C}
          y2={C - R_OUT - 5}
          stroke={color}
          strokeWidth={3}
          strokeLinecap="round"
        />
      )}
    </g>
  );
}

/**
 * Track a continuously-unwound angle so rotation takes the short way round.
 *
 * The unwind happens in an EFFECT, not during render. The previous version
 * mutated a ref while rendering, which React may run twice (StrictMode) or
 * throw away entirely (a bailed-out concurrent render) — either one advanced
 * the accumulated rotation by a phantom step the user never saw.
 */
function useUnwoundAngle(bearing: number): number {
  const [angle, setAngle] = useState(() => normalizeAngle(bearing));
  useEffect(() => {
    setAngle((prev) => {
      const next = unwindAngle(prev, bearing);
      return next === prev ? prev : next;
    });
  }, [bearing]);
  return angle;
}

/** Rotating a rose group is always about its own center. */
const ROTATE_ABOUT_CENTER = applyRotate(C);

function rotateStyle(deg: number): CSSProperties {
  return {
    transform: `rotate(${deg}deg)`,
    transformOrigin: `${C}px ${C}px`,
  };
}

function cx(...parts: Array<string | undefined>): string {
  return parts.filter((p) => p !== undefined && p !== '').join(' ');
}
